import React, { useState, useEffect } from "react";
import "./PrivacyPolicy.css"; 
const sections = [
  { id: "products-services", title: "Products & Services" },
  { id: "eligibility", title: "Eligibility to Use" },
  { id: "account-security", title: "Account & Security" },
  { id: "payment-info", title: "Payment Information" },
  { id: "pricing", title: "Pricing" },
  { id: "cancellation-refunds", title: "Cancellation & Refunds" },
  { id: "user-care", title: "User Care" },
  { id: "use-of-platform", title: "Use of Platform" },
  { id: "ip-rights", title: "Intellectual Property" },
  { id: "disclaimer", title: "Disclaimer of Warranty" },
  { id: "indemnification", title: "Indemnification & Liability" },
  { id: "severability", title: "Severability" },
  { id: "governing-law", title: "Governing Law" },
  { id: "general", title: "General Provisions" },
  { id: "ads", title: "Advertisements" },
  { id: "changes", title: "Changes & Amendments" },
  { id: "acceptance", title: "Acceptance of Terms" },
  { id: "contact", title: "Contacting us" },
];

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState("");

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -80% 0px",
        threshold: 0,
      }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);


  const activeSectionTitle = sections.find(s => s.id === activeSection)?.title || "Navigate to a section...";

  return (
    <div className="legal-page-container">

      <aside className="legal-sidebar">
        <nav>
          <ul>
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={activeSection === section.id ? "active" : ""}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>


      <div className="legal-mobile-nav">
        <button
          className="legal-mobile-nav-trigger"
          onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
          aria-expanded={isMobileNavOpen}
          aria-controls="mobile-nav-menu"
        >
          <span>{activeSectionTitle}</span>
          <svg className={`dropdown-arrow ${isMobileNavOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {isMobileNavOpen && (
          <nav id="mobile-nav-menu" className="legal-mobile-nav-menu">
            <ul>
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={activeSection === section.id ? "active" : ""}
                    onClick={() => setIsMobileNavOpen(false)} 
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>

      <main className="legal-content">
    
        <h1 className="legal-main-heading">Terms & Conditions</h1>
        <p className="legal-text-block">
          These terms and conditions (“Agreement”) set forth the general terms
          and conditions of your use of Vanidya’s technology solutions and
          services (collectively, “Platform”). This Agreement is legally binding
          between you (“User”, “you” or “your”) and Vanidya (“Vanidya”,
          “we”, “us” or “our”). If you are entering into this agreement on
          behalf of a business or other legal entity, you represent that you
          have the authority to bind such entity to this agreement, in which
          case the terms “User”, “you” or “your” shall refer to such entity. If
          you do not have such authority, or if you do not agree with the terms
          of this agreement, you must not accept this agreement and may not
          access and use the Platform. By accessing and using the Platform, you
          acknowledge that you have read, understood, and agree to be bound by
          the terms of this Agreement. You acknowledge that this Agreement is a
          contract between you and Vanidya, even though it is electronic
          and is not physically signed by you, and it governs your use of the
          Platform.
        </p>

        <h2 id="products-services" className="legal-section-heading">
          Products and Services
        </h2>
        <p className="legal-text-block">
          You acknowledge that the Platform enables you to buy/access the
          following Products/Services, including:
        </p>
        <dl className="legal-definition-list">
          <dd>
            A personal, limited, and revocable privilege to use the Platform to
            purchase specific products, including consumer items such as
            groceries and daily consumables ("Products"), from the Platform for
            personal use and not for resale. The Platform does not provide
            services such as product handling or last-mile delivery without the
            purchase of products through the Platform. A fee may be charged for
            these services.
          </dd>
          <dd>
            Quickly and effectively addressing your questions or issues related
            to your Account and the products you have accessed through the
            Platform.
          </dd>
        </dl>
        <p className="legal-text-block">
          Your access to the Platform is a revocable privilege and does not
          allow for resale or commercial use of the Platform or its content. The
          Platform may change the available Products and Services at any time
          without notice. Additional terms and conditions may apply to specific
          product categories, and your purchases will be subject to these terms.
          The Platform determines the regions within India where it offers
          products and services. Before signing up or making a purchase, please
          check if the products are available in your area or city. The Platform
          handles last-mile delivery through third-party providers.
        </p>
        <p className="legal-text-block">
          The Platform refrains from: (i) engaging in any 'unfair trade
          practices' on the Platform or elsewhere regarding the Products and
          Services; (ii) showing bias towards Users of similar groups or making
          arbitrary distinctions among them; or (iii) favoring or discriminating
          against any third-party delivery service providers.
        </p>

        <h2 id="eligibility" className="legal-section-heading">
          Eligibility to Use
        </h2>
        <p className="legal-text-block">
          Platform is not available to minors (those under 18), individuals
          declared insolvent, or anyone who cannot legally enter a contract
          under the Indian Contract Act, 1872, unless otherwise stated. When you
          use the Platform, you're saying you're old enough to make a contract,
          allowed by law to get products from the Platform, and capable of
          making a binding agreement. If you're under 18, you can only use the
          Platform with the explicit consent and supervision of a parent or
          guardian.
        </p>
        <p className="legal-text-block">
          You commit to treating third-party delivery service providers fairly,
          without any discrimination based on race, religion, nationality,
          disability, or any other personal characteristics. If there's proof
          that you've discriminated, like refusing deliveries for these reasons,
          your access to the Platform will be suspended. You can't blame us or
          expect any compensation for being suspended due to discriminatory
          behavior.
        </p>

        <h2 id="account-security" className="legal-section-heading">
          Account and Security
        </h2>
        <p className="legal-text-block">
          To access the Platform, you'll need to sign up and create an account
          by providing the necessary details as mentioned in the Privacy Policy.
        </p>
        <p className="legal-text-block">
          You are responsible for ensuring the accuracy of the information you
          provide. It's essential to ensure that your Account details and all
          other provided information are complete, accurate, and current. If
          there are any changes or if any information is found to be incomplete
          or inaccurate, you must promptly update your Account details on the
          Platform or request Vanidya to make the necessary revisions. If the
          information provided by you is false, inaccurate, unauthorized,
          outdated, or incomplete (or becomes so), we reserve the right to
          refuse access to the Platform. Vanidya may refuse access to the
          Platform without notice if there's a failure to comply with the Terms
          outlined herein or with applicable laws.
        </p>
        <p className="legal-text-block">
          You're responsible for keeping your Account information confidential
          and for all activities carried out on your Account. You agree to
          notify Vanidya immediately of any unauthorized access to your Account.
          Failure to maintain the security and confidentiality of your Account
          information may result in you being held liable for damages caused by
          unauthorized use of your Account.
        </p>

        <h2 id="payment-info" className="legal-section-heading">
          Payment Information
        </h2>
        <p className="legal-text-block">
          The available payment methods on the Platform will be shown during the
          purchasing process.
        </p>
        <p className="legal-text-block">
          You agree that, subject to the Privacy Policy and applicable law, the
          Platform may use third-party vendors and service providers, including
          payment gateways, to process payments and handle payment card
          information. The Platform ensures that such third-party vendors and
          service providers have the necessary licenses.
        </p>
        <p className="legal-text-block">
          To purchase the products, you commit to using and providing valid bank
          details or other required information for making payments ("Payment
          Details"). By providing these Payment Details, you confirm that you're
          legally authorized to do so and to make payments using them, without
          violating any terms and conditions or laws. You can update, add, or
          remove Payment Details through the Platform.
        </p>
        <p className="legal-text-block">
          The Platform is not liable for any payments authorized through the
          Platform using your payment details. Particularly, the Platform is not
          liable for any payments that do not complete, including situations
          where your bank account doesn't have sufficient funds, incorrect
          Payment Details were provided, your payment card expired, or
          circumstances beyond the Platform's control prevent the transaction.
        </p>
        <p className="legal-text-block">
          The Platform is not responsible for unauthorized transactions made
          using your Payment Details and is not obligated to refund any money in
          such cases.
        </p>
        <p className="legal-text-block">
          The payment system provided by the Platform isn't a banking or
          financial service but rather an electronic payment system. Depending
          on your payment method, your issuer may charge fees related to your
          transactions.
        </p>

        <h2 id="pricing" className="legal-section-heading">
          Pricing
        </h2>
        <p className="legal-text-block">
          Ownership of the Products transfers to you upon delivery at the
          specified address/location.
        </p>
        <p className="legal-text-block">
          The prices of Products may vary due to various factors, and you can
          check the prices on the Platform before placing an order. By ordering,
          you agree to pay the prices stated therein. All Products listed on the
          Platform will be sold in Indian Rupees, either at Maximum Retail Price
          (MRP) inclusive of taxes or at a discounted price, unless specified
          otherwise. Prices may change over time, and we strive to offer the
          best prices possible. If there are any unintended errors in Prices or
          other Product information, please let us know, and we'll make the
          necessary corrections.
        </p>
        <p className="legal-text-block">
          During transactions, you'll be informed about all charges, fees, and
          costs, including delivery fees, on the checkout page. We don't
          manipulate the prices of any Products on the Platform.
        </p>

        <h2 id="cancellation-refunds" className="legal-section-heading">
          Cancellation and Refunds
        </h2>
        <p className="legal-text-block">
          You can cancel an order without charge at any time before the Platform
          accepts the order. Once the Platform accepts the order, you cannot
          cancel it. The Platform may cancel an order (fully or partially) due
          to reasons such as shortage or unavailability of certain Products or
          force majeure events. If Platform cancels an order, you will not be
          charged, and any payment already made will be refunded.
        </p>
        <p className="legal-text-block">
          When you cancel or return Products, upon our verification and
          confirmation of the refund request, the refund amount for eligible
          Products will be processed within seven (7) business days. Your refund
          will be credited to the source account. Please note that it may take
          additional time for the refund to reflect in your account, depending
          on your financial institution or payment gateway service provider's
          terms and conditions.
        </p>
        <p className="legal-text-block">
          The terms for accepting returns, cancellations, and refunds may be
          subject to additional conditions communicated to you through push
          notifications on the Platform or other specified modes of
          communication, as determined by the Platform.
        </p>

        <h2 id="user-care" className="legal-section-heading">
          User Care
        </h2>
        <p className="legal-text-block">
          Any warranty or guarantee applicable to the Products is solely that of
          the manufacturer. You can contact the manufacturer directly using the
          customer care details provided on the Product packaging. The Platform
          acts solely as a reseller and disclaims any liability for
          manufacturing defects, quality, taste, or performance of the Products
          sold.
        </p>
        <p className="legal-text-block">
          You understand that the Product images are representations and may not
          reflect the actual Product sold to you. It's important to refer to the
          physical product label for accurate information regarding calorific
          and nutritional value, usage instructions, batch details, manufacture
          date, content, weight, manufacturer, and customer care details before
          consuming the Products. While every effort is made to maintain
          accuracy on the Platform, the actual product packaging may contain
          additional or different information. It's recommended to refer to the
          information presented on the actual product packaging.
        </p>
        <p className="legal-text-block">
          The Platform reserves the right to refuse transactions from Users with
          a history of questionable transactions or breaches of agreements,
          laws, bank charges, or policies, without providing reasons. The
          Platform may conduct checks before approving User orders for security
          reasons. If the Platform is not satisfied with the credibility of the
          User or the transaction's genuineness, it may reject the order, delay
          dispatch, or cancel the transaction at its discretion, especially if
          suspicious activity or high transaction volumes are detected.
        </p>
        <p className="legal-text-block">Beware of Fraud:</p>
        <dl className="legal-definition-list">
          <dd>
            Never share sensitive information like your debit/credit card
            number, CVV number, OTP, UPI/ATM pin, etc., with anyone claiming to
            be a Platform representative.
          </dd>
          <dd>
            Platform representatives will never ask you for such details. Be
            cautious of fraudsters and report incidents immediately to your
            bank, or the nearest police station.
          </dd>
        </dl>
        <p className="legal-text-block">
          For assistance with orders or refund-related issues, visit the 'Get
          Help' section on the Platform.
        </p>
        <p className="legal-text-block">
          Be vigilant in verifying portals/websites claiming to be Vanidya or
          similar, payment links shared on social media, or unsolicited calls
          requesting personal/financial information.
        </p>

        <h2 id="use-of-platform" className="legal-section-heading">
          Use of Platform
        </h2>
        <p className="legal-text-block">
          In addition to other terms as set forth in the Agreement, you are
          prohibited from using the Platform:
        </p>
        <dl className="legal-definition-list">
          <dd>For any unlawful purpose;</dd>
          <dd>
            To solicit others to perform or participate in any unlawful acts;
          </dd>
          <dd>
            To violate any international, federal, provincial or state
            regulations, rules, laws, or local ordinances;
          </dd>
          <dd>
            To infringe upon or violate our intellectual property rights or the
            intellectual property rights of others;
          </dd>
          <dd>
            To harass, abuse, insult, harm, defame, slander, disparage,
            intimidate, or discriminate based on gender, sexual orientation,
            religion, ethnicity, race, age, national origin, or disability;
          </dd>
          <dd>To submit false or misleading information;</dd>
          <dd>
            To upload or transmit viruses or any other type of malicious code
            that will or may be used in any way that will affect the
            functionality or operation of the Platform, third party products and
            services, or the Internet;
          </dd>
          <dd>
            To spam, phish, pharm, pretext, spider, crawl, or scrape; for any
            obscene or immoral purpose; or
          </dd>
          <dd>
            To interfere with or circumvent the security features of the Website
            and Services, third party products and services, or the Internet.
          </dd>
        </dl>
        <p className="legal-text-block">
          We reserve the right to terminate your use of the Platform for
          violating any of the prohibited uses.
        </p>

        <h2 id="ip-rights" className="legal-section-heading">
          Intellectual Property Rights
        </h2>
        <p className="legal-text-block">
          “Intellectual Property Rights” means all present and future rights
          conferred by statute, common law or equity in or in relation to any
          copyright and related rights, trademarks, designs, patents,
          inventions, goodwill and the right to sue for passing off, rights to
          inventions, rights to use, and all other intellectual property rights,
          in each case whether registered or unregistered and including all
          applications and rights to apply for and be granted, rights to claim
          priority from, such rights and all similar or equivalent rights or
          forms of protection and any other results of intellectual activity
          which subsist or will subsist now or in the future in any part of the
          world.
        </p>
        <p className="legal-text-block">
          This Agreement does not transfer to you any intellectual property
          owned by Vanidya or third parties, and all rights, titles, and
          interests in and to such property will remain (as between the parties)
          solely with Vanidya. All trademarks, service marks, graphics and
          logos used in connection with the Platfrom, are trademarks or
          registered trademarks of Vanidya or its licensors. Other
          trademarks, service marks, graphics and logos used in connection with
          the Platform may be the trademarks of other third parties. Your use of
          the Platform grants you no right or license to reproduce or otherwise
          use any of Vanidya or third-party trademarks.
        </p>

        <h2 id="disclaimer" className="legal-section-heading">
          Disclaimer of Warranty
        </h2>
        <p className="legal-text-block">
          You agree that Platform is provided on an “as is” and “as available”
          basis and that your use of the Platform is solely at your own risk. We
          disclaim all warranties of any kind, whether express or implied,
          including the implied warranties of merchantability, fitness for a

          particular purpose and non-infringement. We make no warranty that the
          Platform will meet your requirements, or that the service will be
          uninterrupted, timely, secure, or error-free; nor do we make any
          warranty as to the results that may be obtained from the use of the
          Platform or as to the accuracy or reliability of any information
          obtained through the Platform or that defects in the Platform will be
          corrected.
        </p>
        <p className="legal-text-block">
          You understand and agree that any material and/or data downloaded or
          otherwise obtained using Platform is done at your own discretion and
          risk and that you will be solely responsible for any damage or loss of
          data that results from the download of such material and/or data. We
          make no warranty regarding any goods or services purchased or obtained
          through the Platform or any transactions entered through the Platform
          unless stated otherwise. No advice or information, whether oral or
          written, obtained by you from us or through the Platform shall create
          any warranty not expressly made herein.
        </p>

        <h2 id="indemnification" className="legal-section-heading">
          Indemnification and Limitation of Liability
        </h2>
        <p className="legal-text-block">
          You agree to indemnify and hold Vanidya and its affiliates,
          directors, officers, employees, agents, suppliers and licensors
          harmless from and against any liabilities, losses, damages or costs,
          including reasonable attorneys’ fees, incurred in connection with or
          arising from any third party allegations, claims, actions, disputes,
          or demands asserted against any of them as a result of or relating to
          your Content, your use of the Platform or any willful misconduct on
          your part.
        </p>
        <p className="legal-text-block">
          To the fullest extent permitted by applicable law, in no event will
          Vanidya its affiliates, directors, officers, employees, agents,
          suppliers or licensors be liable to any person for any indirect,
          incidental, special, punitive, cover or consequential damages
          (including, without limitation, damages for lost profits, revenue,
          sales, goodwill, use of content, impact on business, business
          interruption, loss of anticipated savings, loss of business
          opportunity) however caused, under any theory of liability, including,
          without limitation, contract, tort, warranty, breach of statutory
          duty, negligence or otherwise, even if the liable party has been
          advised as to the possibility of such damages or could have foreseen
          such damages. To the maximum extent permitted by applicable law, the
          aggregate liability of Vanidya and its affiliates, officers,
          employees, agents, suppliers, and licensors for any claims arising out
          of or relating to the services shall be limited to the greater of one
          dollar or the total amounts paid by you to Vanidya in the one
          month period preceding the first event giving rise to such liability.
          The limitations and exclusions also apply if this remedy does not
          fully compensate you for any losses or failures of its essential
          purpose.
        </p>

        <h2 id="severability" className="legal-section-heading">
          Severability
        </h2>
        <p className="legal-text-block">
          All rights and restrictions contained in this Agreement may be
          exercised and shall be applicable and binding only to the extent that
          they do not violate any applicable laws and are intended to be limited
          to the extent necessary so that they will not render this Agreement
          illegal, invalid or unenforceable. If any provision or portion of any
          provision of this Agreement shall be held to be illegal, invalid or
          unenforceable by a court of competent jurisdiction, it is the
          intention of the parties that the remaining provisions or portions
          thereof shall constitute their agreement with respect to the subject
          matter hereof, and all such remaining provisions or portions thereof
          shall remain in full force and effect.
        </p>

        <h2 id="governing-law" className="legal-section-heading">
          Governing Law and Jurisdiction
        </h2>
        <p className="legal-text-block">
          The formation, interpretation, and performance of this Agreement and
          any disputes arising out of it shall be governed by the substantive
          and procedural laws of India. The exclusive jurisdiction and venue for
          actions related to the subject matter hereof shall be the courts
          located in Bengaluru, Karnataka, India and you hereby submit to the
          personal jurisdiction of such courts. You hereby waive any right to a
          jury trial in any proceeding arising out of or related to this
          Agreement. The United Nations Convention on Contracts for the
          International Sale of Goods does not apply to this Agreement.
        </p>

        <h2 id="general" className="legal-section-heading">
          General Provisions
        </h2>
        <p className="legal-text-block">
          Notice: The Platform will notify you by email to your registered email
          address, messaging app on your registered mobile phone number, or
          through general notifications on the Platform.
        </p>
        <p className="legal-text-block">
          Assignment: You cannot transfer any rights granted hereunder to any
          third party. The Platform can freely transfer its rights under the
          Terms to its successor or any third party without your consent.
        </p>
        <p className="legal-text-block">
          Force Majeure: The Platform and/or the Platform shall not be liable
          for failure to perform or for delays in providing access to your
          Account or the Platform due to causes beyond its reasonable control,
          such as acts of God, natural disasters, wars, strikes, government
          restrictions, or technical failures.
        </p>

        <h2 id="ads" className="legal-section-heading">
          Advertisements
        </h2>
        <p className="legal-text-block">
          As part of the platform we provide, we may allow third-party
          advertisers ("Third Party Advertisers") to place advertisements on the
          Platform. You understand that any content published by Third Party
          Advertisers is not edited, reviewed, or endorsed by the Platform, and
          we disclaim any liability for such content to the fullest extent
          permitted by law.
        </p>
        <p className="legal-text-block">
          If you are a Third-Party Advertiser, you understand that, in addition
          to these Terms, you must also agree to the Platform's policies and
          other contractual agreements for placing your advertisement.
          Advertisements should not be misleading or in violation of applicable
          laws or guidelines issued by regulatory bodies such as the Advertising
          Standards Council of India. You acknowledge that we have the sole
          discretion to remove any Third-Party Advertisement or request factual
          substantiation if it violates applicable laws or guidelines or is
          misleading.
        </p>
        <p className="legal-text-block">
          If you believe that the content of a third-party advertiser is
          inappropriate or violates applicable laws, please contact us at the
          email address provided.
        </p>

        <h2 id="changes" className="legal-section-heading">
          Changes and amendments
        </h2>
        <p className="legal-text-block">
          We reserve the right to modify this Agreement, or its terms related to
          the Platform at any time at our discretion. We will notify you of any
          changes to this Agreement by sending an email to your registered email
          address. We may also provide notice to you in other ways at our
          discretion, such as through the contact information you have provided.
          An updated version of this Agreement will be effective immediately
          upon the posting of the revised Agreement unless otherwise specified.
          Your continued use of the Website and Services after the effective
          date of the revised Agreement (or such other act specified at that
          time) will constitute your consent to those changes.
        </p>

        <h2 id="acceptance" className="legal-section-heading">
          Acceptance of these Terms
        </h2>
        <p className="legal-text-block">
          You acknowledge that you have read this Agreement and agree to all its
          terms and conditions. By accessing and using the Platform you agree to
          be bound by this Agreement. If you do not agree to abide by the terms
          of this Agreement, you are not authorized to access or use the Website
          and Services.
        </p>

        <h2 id="contact" className="legal-section-heading">
          Contacting us
        </h2>
        <p className="legal-text-block">
          If you have any questions, concerns, or complaints regarding this
          Agreement, we encourage you to contact us at:
        </p>
        <a className="legal-link" href="mailto:info@vanidya.com">
          info@vanidya.com
        </a>
        <p className="legal-text-block legal-update-date">
          This document was last updated on June 18, 2024.
        </p>
      </main>
    </div>
  );
};

export default TermsConditions;